import React, { useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { LayoutConfigurationPage } from '../LayoutConfiguration/layout-configuration-factory.ts';
import { adjustFotoscapesLink, EnhancedLightboxItem, } from '../../net/softbox-types.ts';
import { IInterestResponse } from '@pages/Root/services/api/interest/interest-model-definitions.ts';
import { useSearchParams } from 'react-router';
import { createFeedComponent } from './FeedComponentFactory.tsx';
import TouchableOpacityLinker from '../../../components/TouchableOpacityLinker/TouchableOpacityLinker.tsx';
import FotoscapeLink from '../../../components/FotoscapeLink/FotoscapeLink.tsx';
import Outbrain from '../../components/Outbrain/Outbrain.tsx';
import { resolveOutbrainWidgetId } from '../../components/Outbrain/constants.ts';
import { useInView } from 'react-intersection-observer';
import { filterLayoutConfiguration, onItemClicked, shuffleArray, } from '@/moment/utils/feed-util.ts';
import { fetchPaginatedFeedQuery } from '@/moment/net/feed-fetch-query.ts';
import feedInfiniteLayoutConfiguration from '@moment/layouts/LayoutConfiguration/FeedInfiniteLayoutConfiguration.json';
import { useTranslation } from 'react-i18next';
import { appendQuerystringParams } from '@moment/layouts/MainFeed/utils.ts';
import { AdCounterContext } from '@moment/context/AdCounterContext.tsx';
import { createAdComponent } from '@moment/shared-code/ad-utils.tsx';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import Footer from '@moment/components/Footer/Footer.tsx';
import { isPartnerExternalFotoscapeLink } from '@moment/utils/runtime-config-util.ts';
import { TripodContext } from '@moment/context/TripodContext.tsx';

interface FeedProps {
  interests: IInterestResponse;
  schedule: string;
  partner?: string;
  numberOfPages: number;
}

const infiniteScrollConfiguration =
  feedInfiniteLayoutConfiguration as LayoutConfigurationPage;

export const FeedBuilderInfinite: React.FC<FeedProps> = ({
  schedule,
  interests,
  partner = '',
  numberOfPages,
}) => {
  const queryFn = useCallback(
    ({ pageParam }: { pageParam: number }) => {
      return fetchPaginatedFeedQuery({ interests, schedule, pageParam });
    },
    [interests, schedule]
  );

  const [canLoadMore, setCanLoadMore] = useState(true);
  const [seenUids, setSeenUids] = useState<Map<string, boolean>>(new Map());
  const [renderedPageCount, setRenderedPageCount] = useState(0);
  const [renderedPages, setRenderedPages] = useState<React.ReactNode[]>([]);

  const { fetchNextPage, isFetchingNextPage, data } = useInfiniteQuery({
    queryKey: [infiniteScrollConfiguration.path, schedule, interests],
    queryFn: queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
  });
  const { ref, inView } = useInView();

  const { i18n } = useTranslation();
  const { getAndIncrement } = useContext(AdCounterContext);
  const context = useContext(CommonPageDataContext);
  const language = i18n.language;

  useEffect(() => {
    if (inView && canLoadMore) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, canLoadMore]);

  useEffect(() => {
    if (data?.pages && data.pages.length >= numberOfPages) {
      setCanLoadMore(false);
    }
  }, [data?.pages, numberOfPages]);

  const [searchParams] = useSearchParams();
  const analytics = useAnalytics();

  const pageParams = useMemo(() => {
    return {
      medium: searchParams.get('medium') || 'inapp',
    };
  }, [searchParams]);

  const { tripodData, isLoading: isLoadingTripodData } =
    useContext(TripodContext) || {};

  const useFotoscapeExternalLink = useMemo(() => {
    if (!isLoadingTripodData && !tripodData?.runtimeConfig) {
      return false;
    }
    return isPartnerExternalFotoscapeLink(tripodData, partner);
  }, [isLoadingTripodData, tripodData, partner]);

  const renderPageContent = useCallback(
    (newsItems: EnhancedLightboxItem[]) => {
      const feedComponents = infiniteScrollConfiguration.layoutConfiguration
        .filter(filterLayoutConfiguration(pageParams))
        .map((slot) => {
          if (slot.type === 'ad') {
            const adUnitIndex = getAndIncrement();
            const placement = 'bot'; // All ads in the infinite feed are considered 'bot'

            return createAdComponent({
              adUnitId: `feed-${adUnitIndex}`,
              placement,
              containerClassName: 'ad--block',
              bannerClassName: 'ad--title',
              adBlockClassName: 'feed__ad',
              key: `ad-renderer-${adUnitIndex}`,
            });
          } else if (slot.type === 'item') {
            const { component, item } = createFeedComponent(
              slot,
              newsItems,
              interests,
              TouchableOpacityLinker,
              (item) => {
                const contentLink = ['article', 'standard'].includes(
                  item.lbtype
                )
                  ? appendQuerystringParams(
                      item.linkToContent(partner),
                      searchParams
                    )
                  : item.linkToContent(partner);
                const link = useFotoscapeExternalLink
                  ? contentLink
                  : adjustFotoscapesLink(contentLink);

                setSeenUids((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(item.uid, true);
                  return newMap;
                });
                return {
                  ClickerEl: FotoscapeLink,
                  clickerElProps: {
                    to: link,
                    className: 'moment-link',
                    onClick: onItemClicked(
                      item,
                      analytics,
                      context.partner || '',
                      link,
                      language,
                      searchParams
                    ),
                  },
                };
              },
              i18n
            );
            return <>{component}</>;
          } else if (slot.type === 'outbrain') {
            return (
              <Outbrain
                widgetId={resolveOutbrainWidgetId(slot.settings?.widgetId)}
                hideHeader={Boolean(slot.settings?.hideHeader)}
              />
            );
          }
          return null; // Skip if no news item is available
        })
        .filter((component) => {
          return component !== null;
        }); // Remove null entries

      return newsItems ? feedComponents : <></>;
    },
    [getAndIncrement, i18n, interests, pageParams, partner, searchParams]
  );

  // Function to render a single page
  const renderSinglePage = useCallback(
    (page: any, pageIndex: number) => {
      // Handle null or undefined items gracefully
      const items = page.data?.items || [];
      const uniqueItems = items.filter((item: any) => {
        if (seenUids.has(item.uid)) {
          return false;
        }
        return true;
      });

      const newsItems = shuffleArray(
        uniqueItems.map((it: any) => new EnhancedLightboxItem(it))
      );

      return (
        <div
          key={`feed-page-${pageIndex}`}
          className="news-feed"
          data-testid="news-feed"
        >
          {renderPageContent(newsItems)}
        </div>
      );
    },
    [seenUids, renderPageContent]
  );

  // Track when new pages are available and append them
  useEffect(() => {
    if (data?.pages && data.pages.length > renderedPageCount) {
      const newPages = data.pages.slice(renderedPageCount, numberOfPages);
      const newRenderedPages = newPages.map((page, index) =>
        renderSinglePage(page, renderedPageCount + index)
      );

      setRenderedPages((prev) => [...prev, ...newRenderedPages]);
      setRenderedPageCount(Math.min(data.pages.length, numberOfPages));
    }
  }, [data?.pages, renderedPageCount, renderSinglePage, numberOfPages]);

  return (
    <>
      <div className="news-feeds" data-testid="news-feeds">
        {renderedPages}
      </div>
      <div className="load-more-trigger" data-testid="load-more-trigger">
        <div ref={ref}>
          {isFetchingNextPage && (
            <span
              className="Loading-indicator"
              data-testid="Loading-indicator"
            ></span>
          )}
        </div>
      </div>
      {!canLoadMore && <Footer />}
    </>
  );
};
